import { Help } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { CHIP_COLORS } from '/@/components/chip-label';
import Icon from '/@/components/icon';
import { useGrowersDashboard } from '/@/hooks/dashboards';
import { useIsValidatedAdvisor } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getNodeScopeFilters, type NodeScopeFilters } from '/@/utils/node-scope';
import { reverse } from '/@/utils/router';
import DashboardCard from '../../../../../components/bayer/dashboard-card';

type StatItem = {
    icon: React.ReactNode;
    color: string;
    count: number;
    label: string;
};

const StatRow = ({ icon, color, count, label }: StatItem) => (
    <Stack spacing={1} direction='row' alignItems='center' sx={{ color }}>
        {icon}
        <Typography variant='subtitle2'>
            <Typography variant='subtitle2' fontWeight='bold' component='span'>
                {count}
            </Typography>{' '}
            {label}
        </Typography>
    </Stack>
);

const ColorDot = ({ bg }: { bg: { background: string } }) => (
    <Box
        sx={{
            borderRadius: '50%',
            backgroundColor: bg.background,
            width: '16px',
            height: '16px',
        }}
    />
);

const GrowersDashboardCard = ({
    userNodeId,
    showButton = true,
    nodeScope = getNodeScopeFilters('growers'),
    onHelpClick,
}: {
    userNodeId: number;
    showButton?: boolean;
    nodeScope?: NodeScopeFilters;
    onHelpClick?: () => void;
}) => {
    const navigate = useNavigate();
    const growersDashboardQuery = useGrowersDashboard({
        node: userNodeId,
        ...nodeScope,
    });

    const isAdvisor = useIsValidatedAdvisor();

    return (
        <DashboardCard
            title={_('Productores')}
            headerRightContent={
                onHelpClick ? (
                    <IconButton onClick={onHelpClick} size='small' color='primary'>
                        <Help />
                    </IconButton>
                ) : undefined
            }
            isLoading={growersDashboardQuery.isLoading}
            buttonProps={
                showButton
                    ? {
                          children: _('Ver productores'),
                          onClick: () => {
                              navigate(reverse('Growers.list'));
                          },
                          color: 'secondary',
                      }
                    : undefined
            }
            number={growersDashboardQuery.data?.count}
        >
            <Stack direction='row' spacing={2} flexWrap={'wrap'} useFlexGap>
                {[
                    ...(isAdvisor
                        ? [
                              {
                                  icon: <ColorDot bg={CHIP_COLORS.premiumGrower} />,
                                  color: CHIP_COLORS.premiumGrower.color,
                                  count: growersDashboardQuery.data?.countByType?.premium ?? 0,
                                  label: _('Premium'),
                              },
                              {
                                  icon: <ColorDot bg={CHIP_COLORS.commonGrower} />,
                                  color: CHIP_COLORS.commonGrower.color,
                                  count: growersDashboardQuery.data?.countByType?.common ?? 0,
                                  label: _('Otros segmentos'),
                              },
                          ]
                        : []),
                    {
                        icon: <Icon type='fieldview' size='xxs' sx={{ fill: 'orange' }} padding={false} />,
                        color: 'primary.mui.inactive',
                        count: growersDashboardQuery.data?.withFieldview ?? 0,
                        label: _('Conectados con Fieldview'),
                    },
                ].map((item) => (
                    <StatRow key={item.label} {...item} />
                ))}
            </Stack>
        </DashboardCard>
    );
};

export default GrowersDashboardCard;
